import { model as i18n } from '!/i18n'
import {
  NO_EPG_ID,
  epgProgramsModel,
  inRange,
  type EpgRequestParams,
} from '$/epg/programs'
import { pipe } from '@mobily/ts-belt'
import { combine, createEvent, sample, type Store } from 'effector'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { model as clock } from '~/shared/clock'
import { ONE_HOUR_MS } from '~/shared/constants/time'
import { type LiveProgramLocalizedInfo } from './index.h'
import { formatToLocalizedView } from './l10n'
import { formatToView } from './lib'

export const init = createEvent()

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

// currently playing channel/program info
export const $playing: Store<LiveProgramLocalizedInfo | null> = combine(
  {
    channel: selectEpgChannelModel.$active,
    epg: epgProgramsModel.$epg,
    now: clock.$minutes,
    i18n: i18n.$t,
  },
  ({ channel, epg: [programs], now, i18n: { t } }) => {
    if (channel == null) return null

    const epg = programs.get(channel.epgId ?? NO_EPG_ID)
    if (epg == null) return null

    const isLive = inRange(now)
    const program = epg.find((p) => isLive([p.actualStartMs, p.actualStopMs]))
    if (program == null) return null

    return pipe(
      { channel, program },
      formatToView,
      formatToLocalizedView({ now, t })
    )
  }
)

// try to request channel's epg data if it's not loaded yet (or I suspect it's not loaded yet)
sample({
  clock: [selectEpgChannelModel.$active, clock.$minutes],
  source: {
    channel: selectEpgChannelModel.$active,
    epg: epgProgramsModel.$epg,
    now: clock.$minutes,
  },
  filter: ({ channel, epg: [programs], now }) => {
    if (channel == null) return false

    const epg = programs.get(channel.epgId ?? NO_EPG_ID)
    if (epg == null) return true // <- try to load epg for channel

    const isLive = inRange(now)
    const program = epg.find((p) => isLive([p.actualStartMs, p.actualStopMs]))
    return program == null // <- try to load epg for channel
  },
  fn: ({ channel, now }) =>
    ({
      channels: [channel!],
      period: [now, now + ONE_HOUR_MS], // request programs for 1 hour from now
    }) as EpgRequestParams,
  target: epgProgramsModel.fetchEpg,
})

//
// left and reset logic
//

// do nothing
